<script setup lang="ts">
import targetIcon from '~/assets/img/icon/target.svg';
import LocationAutocomplete from '~/components/LocationAutocomplete.vue';
import SearchHistoryAutocomplete from '~/components/SearchHistoryAutocomplete.vue';
import { DISTANCE_OPTIONS } from '~/utils/constants/SearchOptions';

const searchQuery = ref(null);
const location = ref<{ place_id?: string; formatted_address?: string; description?: string }>({ description: '' });
const distance = ref(null);

// Check if location is empty
const isLocationEmpty = computed(() => {
  const loc = location.value;
  return !loc?.place_id;
});

// Recent searches management
const RECENT_SEARCHES_KEY = 'recentSearches';
const MAX_RECENT_SEARCHES = 5;

// Save search to recent searches
const saveToRecentSearches = (query: string) => {
  if (!query || query.trim() === '') return;

  const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
  const searches = stored ? JSON.parse(stored) : [];

  // Remove if already exists
  const filtered = searches.filter((s: string) => s !== query);

  // Add to beginning
  const updated = [query, ...filtered].slice(0, MAX_RECENT_SEARCHES);

  // Save to localStorage
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
};

const router = useRouter();

const handleSearch = async (
  searchLocation?: { place_id?: string; formatted_address?: string; description?: string } | null
) => {
  // Save current search query to recent searches (only if not empty)
  if (searchQuery.value && searchQuery.value.trim() !== '') {
    saveToRecentSearches(searchQuery.value);
  }

  // Navigate to search results page with query parameters (including empty search)
  router.push({
    path: '/search-results',
    query: {
      search: searchQuery.value || undefined,
      location:
        searchLocation?.formatted_address ||
        searchLocation?.description ||
        location.value?.formatted_address ||
        location.value?.description ||
        undefined,
      place_id: searchLocation?.place_id || location.value?.place_id || undefined,
      distance: distance.value || undefined,
    },
  });
};

// Watch location and reset distance when location is cleared
watch(
  location,
  (newLocation) => {
    const isEmpty = !newLocation?.description && !newLocation?.formatted_address && !newLocation?.place_id;
    if (isEmpty) {
      distance.value = null;
    }
  },
  { deep: true }
);
</script>

<template>
  <v-container fluid class="hero-search-bg rounded-xl py-md-12 px-md-10 px-md-16 pa-8 pa-xs-6">
    <v-row>
      <v-col>
        <h1 class="text-white font-title-4 mb-4">Find Earthmoving Listings Near You</h1>
        <p class="text-white mb-8">Browse jobs, hire machines, or find repair services in your area.</p>

        <!-- Search Bar - Desktop -->
        <v-card class="rounded-pill d-none d-md-flex align-center">
          <SearchHistoryAutocomplete
            v-model="searchQuery"
            placeholder="e.g. 'Backyard levelling'"
            variant="plain"
            density="compact"
            class="flex-grow-1 ml-6 my-4 mb-5"
            @search="handleSearch"
          />
          <v-divider vertical class="mx-2" />
          <div style="max-width: 320px; min-width: 320px">
            <LocationAutocomplete
              v-model="location"
              label=""
              placeholder="Location"
              variant="plain"
              density="compact"
              hide-details
              class="hero-location-autocomplete"
              @update:model-value="handleSearch($event)"
            />
          </div>
          <v-divider vertical class="mx-2" />
          <v-select
            v-model="distance"
            :items="DISTANCE_OPTIONS"
            item-title="title"
            item-value="value"
            label="Distance"
            variant="plain"
            hide-details
            density="compact"
            class="ml-2 hero-search__distance-select"
            style="min-width: 180px; max-width: 180px"
            :menu-props="{ offset: 24 }"
            :disabled="isLocationEmpty"
          />
          <v-divider vertical class="mx-2" />
          <v-btn
            class="ml-2 mr-4"
            color="yellow-darken-1"
            size="large"
            prepend-icon="mdi-magnify"
            @click="handleSearch"
          >
            Search
          </v-btn>
        </v-card>

        <!-- Search Bar - Mobile/Tablet -->
        <div class="d-md-none">
          <v-card class="pa-0 rounded-xl">
            <v-card-text class="pa-0">
              <v-row no-gutters>
                <v-col cols="12" md="12" sm="12">
                  <SearchHistoryAutocomplete
                    v-model="searchQuery"
                    placeholder="e.g. 'Backyard levelling'"
                    variant="plain"
                    density="comfortable"
                    class="mx-6 mt-3 mb-4"
                    @search="handleSearch"
                  />
                  <v-divider />
                </v-col>
                <v-col cols="12" md="6" sm="6">
                  <div class="pa-0 mt-3 mb-4 mx-6">
                    <LocationAutocomplete
                      v-model="location"
                      label=""
                      placeholder="Location"
                      variant="plain"
                      density="compact"
                      hide-details
                      @update:model-value="handleSearch($event)"
                    />
                  </div>
                  <v-divider />
                </v-col>
                <v-col cols="12" md="6" sm="6">
                  <div class="pa-0 mb-1 mt-2 mx-2">
                    <v-select
                      v-model="distance"
                      :items="DISTANCE_OPTIONS"
                      item-title="title"
                      item-value="value"
                      variant="plain"
                      hide-details
                      label=""
                      placeholder="Distance"
                      class="mb-4 mx-4 hero-search__distance-select"
                      density="comfortable"
                      :menu-props="{ offset: 24 }"
                      :disabled="isLocationEmpty"
                    >
                      <template #prepend-inner>
                        <img :src="targetIcon" alt="Distance" width="24" height="24" />
                      </template>
                    </v-select>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <v-btn
            color="yellow-darken-1"
            block
            class="mt-5 pa-0 mobile-search-btn"
            size="x-large"
            prepend-icon="mdi-magnify"
            @click="handleSearch"
          >
            Search
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped lang="scss">
.hero-search-bg {
  background-image: url('~/assets/img/landing/searchbox_bg.png');
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  min-height: 268px;

  @media (max-width: 960px) {
    background-size: cover;
    background-position: center;
  }

  @media (max-width: 600px) {
    min-height: 380px;
  }
}

.mobile-search-btn {
  padding: 0 !important;
  height: 48px !important;
}

// Hide label on focus for v-select and v-autocomplete in HeroSearch
:deep(.v-select),
:deep(.v-autocomplete) {
  .v-field--focused .v-label,
  .v-field--active .v-label {
    opacity: 0 !important;
  }

  input::placeholder {
    color: #414156 !important;
    opacity: 1 !important;
  }

  .v-field__input::placeholder {
    color: #414156 !important;
    opacity: 1 !important;
  }
}

// Style disabled state - needs to be outside to work properly
:deep(.v-input),
:deep(.v-select.v-field--disabled),
:deep(.v-autocomplete.v-field--disabled) {
  // Target all text elements
  .v-field__input,
  .v-field__input input,
  .v-field__input::placeholder,
  input::placeholder,
  .v-select__selection-text,
  .v-field-label,
  .v-label,
  .v-field-label v-label,
  .v-text-field__prefix,
  .v-text-field__suffix {
    color: #414156 !important;
    opacity: 1 !important;
  }
}

// Fix disabled state for HeroSearch distance select specifically with :deep
:deep(.hero-search__distance-select.v-select .v-field.v-field--disabled) {
  color: #414156 !important;
  opacity: 1 !important;

  .v-field__input,
  .v-field__input input,
  .v-field__input::placeholder,
  input::placeholder,
  .v-select__selection-text,
  .v-field-label,
  .v-label,
  .v-field-label v-label,
  .v-text-field__prefix,
  .v-text-field__suffix {
    color: #414156 !important;
    opacity: 1 !important;
  }

  // Target the prepend-inner icon (target icon) when disabled
  .v-field__prepend-inner {
    img,
    svg,
    i,
    .v-icon {
      opacity: 1 !important;
      color: #000000 !important;
    }
  }

  // Target the append-inner (dropdown arrow) when disabled
  .v-field__append-inner {
    i,
    .v-icon,
    svg {
      opacity: 1 !important;
      color: #000000 !important;
    }
  }
}

// Make LocationAutocomplete background transparent in HeroSearch
.hero-location-autocomplete :deep(.v-field) {
  background-color: transparent !important;
}
</style>
